import React, { useMemo } from 'react';
import { Box, Tab, Text } from '@clariness/clarikit-react';
import { useMultiStep } from 'components/MultiStep';
import { useIntl } from 'react-intl';
import { captureEventV2 } from 'utils/events/captureEvent';
import { Events } from '@clariness/tracking-event-runtime';
import { Store } from 'store';

interface Props {
  patientName: string;
  patientID?: number;
  studyID?: string;
  siteID?: string;
  isExternalReferral?: boolean;
}

const PROFILE_TAB = 'profile';
const SCREENER_TAB = 'screener';
const ACTIVITY_TAB = 'activity';

export const PatientDetailsTabMenu: React.FC<Props> = ({
  patientName,
  patientID,
  siteID,
  studyID,
  isExternalReferral = false,
}: Props) => {
  const intl = useIntl();
  const { activeStep, gotoStep } = useMultiStep();
  const { user } = Store.useUser();

  const PROFILE_STEP = 1;
  const SCREENER_STEP = 2;
  // External referrals skip the Screener step, so Activity moves up by one.
  const ACTIVITY_STEP = isExternalReferral ? 2 : 3;

  const tabs = useMemo(
    () =>
      [
        {
          id: PROFILE_TAB,
          label: intl.formatMessage({
            id: 'is.patient_details_modal.profile_tab',
            defaultMessage: 'Profile',
          }),
        },
        !isExternalReferral && {
          id: SCREENER_TAB,
          label: intl.formatMessage({
            id: 'is.patient_details_modal.screener_tab',
            defaultMessage: 'Screener',
          }),
        },
        {
          id: ACTIVITY_TAB,
          label: intl.formatMessage({
            id: 'is.patient_details_modal.activity_tab',
            defaultMessage: 'Activity',
          }),
        },
      ].filter(Boolean) as { id: string; label: string }[],
    [intl, isExternalReferral]
  );

  const selectedTab =
    activeStep === PROFILE_STEP
      ? PROFILE_TAB
      : activeStep === SCREENER_STEP && !isExternalReferral
        ? SCREENER_TAB
        : ACTIVITY_TAB;

  const handleTabSelect = (id: string) => {
    if (id === PROFILE_TAB) {
      captureEventV2(
        Events.InvestigatorService.InvestigatorViewedPatientProfile({
          InvestigatorID: user?.id ?? '',
          SiteID: siteID || '',
          PatientInternalID: patientID ?? 0,
        })
      );
      gotoStep(PROFILE_STEP);
      return;
    }

    if (id === SCREENER_TAB) {
      captureEventV2(
        Events.InvestigatorService.InvestigatorCheckedScreener({
          InvestigatorID: user?.id ?? '',
          SiteID: siteID || '',
          StudyID: studyID || '',
        })
      );
      gotoStep(SCREENER_STEP);
      return;
    }

    captureEventV2(
      Events.InvestigatorService.InvestigatorViewedPatientActivity({
        InvestigatorID: user?.id ?? '',
      })
    );
    gotoStep(ACTIVITY_STEP);
  };

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      style={{ marginBottom: '32px' }}
    >
      <style>
        {`
          .patientDetailsTabs .Clarikit-Tab--tabButton {
            font-size: var(--c-font-size-24);
          }
        `}
      </style>
      <Text as="p" variant="headingSm">
        {patientName} ({patientID})
      </Text>
      <Box className="patientDetailsTabs">
        <Tab
          tabs={tabs}
          selectedTab={selectedTab}
          onTabSelect={handleTabSelect}
          ariaLabel={intl.formatMessage({
            id: 'is.patient_details_modal.tabs_aria_label',
            defaultMessage: 'Patient details sections',
          })}
        />
      </Box>
    </Box>
  );
};
